import { api } from './axios';

export type AccountType = 'ASSET' | 'LIABILITY' | 'INCOME' | 'EXPENSE' | 'EQUITY';
export type EntryType = 'DEBIT' | 'CREDIT';
export type SourceType =
  | 'FEE_RECEIPT'
  | 'EXPENSE'
  | 'SALARY'
  | 'STORE_SALE'
  | 'STORE_PURCHASE'
  | 'VENDOR_PAYMENT'
  | 'OTHER_INCOME'
  | 'JOURNAL';
export type ExpensePaymentType = 'CASH' | 'CREDIT' | 'BANK';
export type IncomePaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'ONLINE_TRANSFER';

export interface Account {
  id: number;
  code: string;
  name: string;
  accountType: AccountType;
  parentId: number | null;
  postingAccount: boolean;
  system: boolean;
  active: boolean;
}

export interface AccountTreeNode {
  account: Account;
  children: AccountTreeNode[];
}

export interface AccountInput {
  code: string;
  name: string;
  accountType: AccountType;
  parentId?: number | null;
  postingAccount: boolean;
}

export interface BankAccount {
  id: number;
  accountName: string;
  bankName: string | null;
  accountNumber: string | null;
  branch: string | null;
  cash: boolean;
  glAccountId: number;
  glAccountName: string | null;
  openingBalance: string;
  openingBalanceDate: string;
  currentBalance: string;
  active: boolean;
}

export interface BankAccountInput {
  accountName: string;
  bankName?: string;
  accountNumber?: string;
  branch?: string;
  cash: boolean;
  glAccountId: number;
  openingBalance: string;
  openingBalanceDate: string;
}

export interface Expense {
  id: number;
  expenseDate: string;
  expenseCategoryId: number;
  expenseCategoryName: string | null;
  amount: string;
  description: string;
  vendorId: number | null;
  paymentType: ExpensePaymentType;
  bankAccountId: number;
  bankAccountName: string | null;
  glAccountId: number | null;
  glAccountName: string | null;
  requiresApproval: boolean;
  approved: boolean;
  approvedBy: number | null;
  approvedAt: string | null;
  reversed: boolean;
  reversalReason: string | null;
}

export interface ExpenseInput {
  expenseDate: string;
  expenseCategoryId: number;
  amount: string;
  description: string;
  paymentType: ExpensePaymentType;
  bankAccountId: number;
  glAccountId: number;
}

export interface OtherIncome {
  id: number;
  incomeDate: string;
  incomeCategoryId: number;
  incomeCategoryName: string | null;
  amount: string;
  description: string;
  paymentMethod: IncomePaymentMethod;
  bankAccountId: number;
  bankAccountName: string | null;
  glAccountId: number | null;
  glAccountName: string | null;
  reference: string | null;
  reversed: boolean;
  reversalReason: string | null;
}

export interface OtherIncomeInput {
  incomeDate: string;
  incomeCategoryId: number;
  amount: string;
  description: string;
  paymentMethod: IncomePaymentMethod;
  bankAccountId: number;
  glAccountId: number;
  reference?: string;
}

export interface JournalLine {
  id: number;
  accountId: number;
  accountCode: string | null;
  accountName: string | null;
  entryType: EntryType;
  amount: string;
  description: string | null;
}

export interface JournalVoucher {
  id: number;
  voucherNumber: number;
  voucherDate: string;
  description: string;
  totalDebit: string;
  totalCredit: string;
  reversed: boolean;
  reversalReason: string | null;
  reversalVoucherId: number | null;
  lines: JournalLine[];
}

export interface JournalVoucherInput {
  voucherDate: string;
  description: string;
  lines: { accountId: number; entryType: EntryType; amount: string; description?: string }[];
}

export interface LedgerEntry {
  id: number;
  accountId: number;
  accountCode: string | null;
  accountName: string | null;
  entryDate: string;
  entryType: EntryType;
  amount: string;
  sourceType: SourceType;
  sourceId: number;
  description: string | null;
  bankAccountId: number | null;
  reversal: boolean;
  originalEntryId: number | null;
  reconciled: boolean;
}

export interface TrialBalanceRow {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType: AccountType;
  totalDebit: string;
  totalCredit: string;
  balance: string;
}

export interface TrialBalance {
  asOf: string;
  rows: TrialBalanceRow[];
}

export const financeApi = {
  // accounts
  listAccounts: () => api.get<Account[]>('/finance/accounts').then((r) => r.data),
  accountTree: () => api.get<AccountTreeNode[]>('/finance/accounts/tree').then((r) => r.data),
  createAccount: (body: AccountInput) =>
    api.post<Account>('/finance/accounts', body).then((r) => r.data),
  updateAccount: (id: number, body: AccountInput) =>
    api.put<Account>(`/finance/accounts/${id}`, body).then((r) => r.data),
  toggleAccount: (id: number, value: boolean) =>
    api.patch<Account>(`/finance/accounts/${id}/active`, null, { params: { value } }).then((r) => r.data),
  deleteAccount: (id: number) => api.delete<void>(`/finance/accounts/${id}`).then((r) => r.data),

  // bank accounts
  listBankAccounts: () => api.get<BankAccount[]>('/finance/bank-accounts').then((r) => r.data),
  createBankAccount: (body: BankAccountInput) =>
    api.post<BankAccount>('/finance/bank-accounts', body).then((r) => r.data),
  updateBankAccount: (id: number, body: BankAccountInput) =>
    api.put<BankAccount>(`/finance/bank-accounts/${id}`, body).then((r) => r.data),
  toggleBankAccount: (id: number, value: boolean) =>
    api.patch<BankAccount>(`/finance/bank-accounts/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // expenses
  listExpenses: (from: string, to: string) =>
    api.get<Expense[]>('/finance/expenses', { params: { from, to } }).then((r) => r.data),
  addExpense: (body: ExpenseInput) =>
    api.post<Expense>('/finance/expenses', body).then((r) => r.data),
  reverseExpense: (id: number, reason: string) =>
    api.post<Expense>(`/finance/expenses/${id}/reverse`, { reason }).then((r) => r.data),
  approveExpense: (id: number, glAccountId: number) =>
    api.post<Expense>(`/finance/expenses/${id}/approve`, { glAccountId }).then((r) => r.data),

  // other income
  listOtherIncome: (from: string, to: string) =>
    api.get<OtherIncome[]>('/finance/other-income', { params: { from, to } }).then((r) => r.data),
  addOtherIncome: (body: OtherIncomeInput) =>
    api.post<OtherIncome>('/finance/other-income', body).then((r) => r.data),
  reverseOtherIncome: (id: number, reason: string) =>
    api.post<OtherIncome>(`/finance/other-income/${id}/reverse`, { reason }).then((r) => r.data),

  // journals
  listJournals: (from: string, to: string) =>
    api.get<JournalVoucher[]>('/finance/journals', { params: { from, to } }).then((r) => r.data),
  getJournal: (id: number) =>
    api.get<JournalVoucher>(`/finance/journals/${id}`).then((r) => r.data),
  createJournal: (body: JournalVoucherInput) =>
    api.post<JournalVoucher>('/finance/journals', body).then((r) => r.data),
  reverseJournal: (id: number, reason: string) =>
    api.post<JournalVoucher>(`/finance/journals/${id}/reverse`, { reason }).then((r) => r.data),

  // daybook + trial balance
  daybook: (params: { from: string; to: string; accountId?: number }) =>
    api.get<LedgerEntry[]>('/finance/daybook', { params }).then((r) => r.data),
  trialBalance: (asOf: string) =>
    api.get<TrialBalance>('/finance/trial-balance', { params: { asOf } }).then((r) => r.data),

  // bank reconciliation
  reconciliation: (params: { bankAccountId: number; from: string; to: string; filter?: ReconciliationFilter }) =>
    api.get<ReconciliationView>('/finance/reconciliation', { params }).then((r) => r.data),
  markReconciled: (entryIds: number[], reconciled: boolean) =>
    api
      .post<number>('/finance/reconciliation/mark', { entryIds, reconciled })
      .then((r) => r.data),

  // financial statements
  incomeExpenditure: (from: string, to: string) =>
    api.get<IncomeExpenditureStatement>('/finance/income-expenditure', { params: { from, to } }).then((r) => r.data),
  balanceSheet: (asOf: string) =>
    api.get<BalanceSheet>('/finance/balance-sheet', { params: { asOf } }).then((r) => r.data),
  bankBook: (bankAccountId: number, from: string, to: string) =>
    api.get<BankBook>('/finance/bank-book', { params: { bankAccountId, from, to } }).then((r) => r.data),

  // cash handover
  cashHandoverSummary: (bankAccountId: number, date: string) =>
    api
      .get<CashHandoverSummary>('/finance/cash-handovers/summary', {
        params: { bankAccountId, date },
      })
      .then((r) => r.data),
  listCashHandovers: (from: string, to: string) =>
    api.get<CashHandover[]>('/finance/cash-handovers', { params: { from, to } }).then((r) => r.data),
  createCashHandover: (body: CashHandoverInput) =>
    api.post<CashHandover>('/finance/cash-handovers', body).then((r) => r.data),
  deleteCashHandover: (id: number) =>
    api.delete<void>(`/finance/cash-handovers/${id}`).then((r) => r.data),
};

export interface StatementLine {
  accountId: number | null;
  accountCode: string | null;
  accountName: string;
  amount: string;
}

export interface IncomeExpenditureStatement {
  from: string;
  to: string;
  incomeLines: StatementLine[];
  totalIncome: string;
  expenseLines: StatementLine[];
  totalExpense: string;
  netSurplus: string;
}

export interface BalanceSheet {
  asOf: string;
  assets: StatementLine[];
  totalAssets: string;
  liabilities: StatementLine[];
  totalLiabilities: string;
  equity: StatementLine[];
  totalEquity: string;
  totalLiabilitiesAndEquity: string;
  variance: string;
}

export interface BankBookRow {
  entryId: number;
  entryDate: string;
  description: string | null;
  accountName: string | null;
  entryType: 'DEBIT' | 'CREDIT';
  amount: string;
  sourceType: string;
  sourceId: number;
  reversal: boolean;
  runningBalance: string;
}

export interface BankBook {
  bankAccountId: number;
  bankAccountName: string;
  cash: boolean;
  from: string;
  to: string;
  openingBalance: string;
  closingBalance: string;
  totalDebits: string;
  totalCredits: string;
  rows: BankBookRow[];
}

export type ReconciliationFilter = 'ALL' | 'RECONCILED' | 'UNRECONCILED';

export interface ReconciliationRow {
  entryId: number;
  entryDate: string;
  description: string | null;
  accountName: string | null;
  entryType: 'DEBIT' | 'CREDIT';
  amount: string;
  sourceType: string;
  sourceId: number;
  reversal: boolean;
  reconciled: boolean;
}

export interface ReconciliationView {
  bankAccountId: number;
  bankAccountName: string;
  from: string;
  to: string;
  openingBalance: string;
  bookBalance: string;
  reconciledBalance: string;
  unreconciledTotal: string;
  unreconciledCount: number;
  rows: ReconciliationRow[];
}

export interface CashHandoverSummary {
  bankAccountId: number;
  bankAccountName: string;
  date: string;
  openingBalance: string;
  totalReceipts: string;
  totalPayments: string;
  expectedAmount: string;
}

export interface CashHandover {
  id: number;
  voucherNumber: string;
  bankAccountId: number;
  bankAccountName: string | null;
  handoverDate: string;
  cashierUserId: number;
  cashierName: string;
  receivedBy: string;
  openingBalance: string;
  totalReceipts: string;
  totalPayments: string;
  expectedAmount: string;
  handedAmount: string;
  variance: string;
  notes: string | null;
  createdAt: string;
}

export interface CashHandoverInput {
  bankAccountId: number;
  handoverDate: string;
  receivedBy: string;
  handedAmount: string;
  notes?: string;
}
